/**
 * @praesto/core/storage — Cloud Storage wrapper, tenant-scoped paths.
 *
 * Storage layout (matches `firestore/storage.rules`):
 *
 *   gs://{bucket}/{tenantId}/{module}/{subpath}
 *
 * The wrapper enforces the tenant prefix automatically (the caller
 * passes `module` + `subpath`, never the full path) so rule-required
 * isolation is impossible to bypass from the UI layer.
 *
 * V1: `upload` returns a `StorageRef`. `signedUrl` returns a plain
 * `getDownloadURL` URL — the staff and managers reading checklist
 * photos are all `isMember(tenantId)` already, so the rule check on
 * the bucket suffices; true signed URLs (time-bounded) come with
 * the admin-only views in V3 (e.g. cross-tenant superadmin support).
 */
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import type { StorageRef, StorageService, StorageUploadOpts } from "@praesto/contracts";
import type { RuntimeContext } from "../internal/context";

export function createStorageService(ctx: RuntimeContext): StorageService {
  const { storage } = ctx.firebase;

  return {
    async upload(file: File | Blob, opts: StorageUploadOpts): Promise<StorageRef> {
      const path = `${ctx.tenantId}/${opts.module}/${opts.subpath}`;
      const r = storageRef(storage, path);
      const result = await uploadBytes(r, file);
      const contentType =
        result.metadata.contentType ??
        (file instanceof File ? file.type : "application/octet-stream");
      return {
        bucket: result.ref.bucket,
        path: result.ref.fullPath,
        contentType,
        sizeBytes: result.metadata.size,
      };
    },

    async signedUrl(ref: StorageRef): Promise<string> {
      const r = storageRef(storage, ref.path);
      return getDownloadURL(r);
    },

    async delete(ref: StorageRef): Promise<void> {
      const r = storageRef(storage, ref.path);
      await deleteObject(r);
    },
  };
}
